import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import { VendorDashboardPage } from "./pages/VendorDashboardPage";
import { QuoteResponseEmbedPage } from "./pages/QuoteResponseEmbedPage";
import { ChatEmbedPage } from "./features/chat";
import { InventoryEmbedPage } from "./pages/InventoryEmbedPage";
import { AutoQuoteEmbedPage } from "./pages/AutoQuoteEmbedPage";
import {
  SupplierLoginPage,
  SupplierAuthProvider,
  ProductVersionLabel,
} from "@semo/shared-ui";
import { DashboardAuthProvider, EmbedAuthProvider } from "./features/auth";
import { useVendorAuth } from "./features/auth/application/hooks/useVendorAuth";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { safeReturnPath } from "./utils/returnPath";
import { isDashboardEnabled } from "./utils/dashboardMode";
import { RespondedProjectDetailPage } from "./features/quote-requests/presentation/pages/RespondedProjectDetailPage";
import { ChatRealtimeLoader } from "./components/ChatRealtimeLoader";
import { DevCostEstimatePreview } from "./pages/DevCostEstimatePreview";
import { DevChatListPreview } from "./pages/DevChatListPreview";
import { DevSettlementCalendarPreview } from "./pages/DevSettlementCalendarPreview";

/**
 * Public Route Wrapper - Redirects to home if already logged in
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, isSessionLoading } = useVendorAuth();
  const location = useLocation();

  if (isSessionLoading) return null; // Prevent flash
  if (currentUser) {
    // 로그인 전에 열려던 화면(주소 직접 접근·세션 만료)으로 돌려보낸다(A3-28).
    const state: unknown = location.state;
    const from =
      typeof state === "object" && state !== null && "from" in state
        ? safeReturnPath(state.from)
        : null;
    return <Navigate to={from ?? "/"} replace />;
  }

  return <>{children}</>;
}

/**
 * Login Page with its own provider
 */
function LoginPage() {
  const { checkSession } = useVendorAuth();
  return (
    <SupplierAuthProvider>
      <SupplierLoginPage onSuccess={() => checkSession()} />
      {/* 제품명·버전(GS 검수 A4-15) — 로그인 화면 아래 */}
      <ProductVersionLabel className="text-center pb-6 -mt-2 bg-muted/30" />
    </SupplierAuthProvider>
  );
}

function App() {
  // Default to Embed-Only in Production unless VITE_ENABLE_DASHBOARD is explicitly 'true'
  const IS_EMBED_ONLY_MODE = !isDashboardEnabled();

  return (
    <BrowserRouter>
      <Toaster richColors position="top-right" />
      <Routes>
        {/* =========================================================
            DEV-ONLY PREVIEW (로그인 없이 원가계산서 탭 미리보기)
            import.meta.env.DEV 에서만 존재 → 프로덕션 빌드 미포함
            ========================================================= */}
        {import.meta.env.DEV && (
          <Route path="/dev/preview" element={<DevCostEstimatePreview />} />
        )}
        {import.meta.env.DEV && (
          <Route path="/dev/chat-preview" element={<DevChatListPreview />} />
        )}
        {import.meta.env.DEV && (
          <Route
            path="/dev/settlement-calendar"
            element={<DevSettlementCalendarPreview />}
          />
        )}

        {/* =========================================================
            DASHBOARD ROUTES (Session Cookie / LocalStorage Auth)
            ========================================================= */}
        {!IS_EMBED_ONLY_MODE && (
          <Route
            element={
              <DashboardAuthProvider>
                <ChatRealtimeLoader />
                <Outlet />
              </DashboardAuthProvider>
            }
          >
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <VendorDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote-requests/:requestId"
              element={
                <ProtectedRoute>
                  <RespondedProjectDetailPage />
                </ProtectedRoute>
              }
            />
          </Route>
        )}

        {/* =========================================================
            EMBED ROUTES (URL Token Auth)
            ========================================================= */}
        <Route
          element={
            <EmbedAuthProvider>
              <ChatRealtimeLoader />
              <Outlet />
            </EmbedAuthProvider>
          }
        >
          {/* Quote Request / Response (AlimTalk Link: /requests/:id) */}
          <Route
            path="/requests/:quoteRequestId"
            element={<QuoteResponseEmbedPage />}
          />
          <Route
            path="/embed/quote-response/:quoteRequestId"
            element={<QuoteResponseEmbedPage />}
          />
          <Route
            path="/quote-response/:quoteRequestId"
            element={<QuoteResponseEmbedPage />}
          />
          <Route
            path="/quote/:quoteRequestId"
            element={<QuoteResponseEmbedPage />}
          />

          {/* Contract Detail (AlimTalk Link: /contracts/:id) - Opens Dashboard > Contracts Tab > Detail Modal */}
          <Route
            path="/contracts/:contractId"
            element={<InventoryEmbedPage initialTab="contracts" />}
          />

          {/* Settlements (AlimTalk Link: /settlements) - Opens Dashboard > Settlements Tab */}
          <Route
            path="/settlements"
            element={<InventoryEmbedPage initialTab="settlements" />}
          />

          {/* Chat (AlimTalk Link: /chat/:roomId) */}
          <Route path="/embed/chat/:chatRoomId" element={<ChatEmbedPage />} />
          <Route path="/chat/:chatRoomId" element={<ChatEmbedPage />} />

          {/* 2026-07-31: c-market 참여현황 [견적서] → 대시보드 위 상세 모달 자동 오픈.
               InventoryEmbedPage 가 requestId 를 감지해 detailFromEmbed 플래그로
               발주처 정보 노출/1:1 채팅 버튼 숨김 처리를 적용한다. */}
          <Route
            path="/embed/quote-detail/:requestId"
            element={<InventoryEmbedPage />}
          />
          <Route
            path="/quote-detail/:requestId"
            element={<InventoryEmbedPage />}
          />

          <Route path="/embed/dashboard" element={<InventoryEmbedPage />} />
          <Route path="/auto-quote" element={<AutoQuoteEmbedPage />} />
          <Route path="/embed/auto-quote" element={<AutoQuoteEmbedPage />} />
        </Route>

        {/* Fallback */}
        <Route
          path="*"
          element={
            IS_EMBED_ONLY_MODE ? (
              <div className="flex h-screen w-full items-center justify-center bg-gray-50 text-gray-500">
                Embed Only Mode
              </div>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
